import { ChevronDown } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Questions fréquentes.
 *
 * Bâtie sur <details>/<summary> natifs : ouverture au clavier, lisible par les
 * lecteurs d'écran et trouvable par la recherche du navigateur (Ctrl+F trouve
 * même le texte replié), sans une ligne de JavaScript.
 */
const QUESTIONS = {
  fr: [
    {
      q: "Les mesures satellite sont-elles fiables ?",
      r: "Le toit est mesuré à partir de l'imagerie satellite et de la pente réelle du bâtiment, pas d'une estimation à vue. Et rien n'est imposé : le contour reste modifiable à la main avant de produire la soumission, comme la pente. Vous gardez le dernier mot sur chaque chiffre.",
    },
    {
      q: "Puis-je essayer avant de payer ?",
      r: "Oui. L'estimateur de la page d'accueil est le vrai outil, sur un vrai compte : entrez une adresse que vous connaissez et comparez le résultat à ce que vous auriez chiffré. Aucune carte de crédit, aucune inscription.",
    },
    {
      q: "Que se passe-t-il si je dépasse mon nombre d'estimations ?",
      r: "C'est vous qui décidez. Par défaut les estimations supplémentaires passent, facturées au tarif de dépassement de votre forfait — de 0,50 $ à 2 $ selon le forfait — parce que perdre un contrat pour un compteur à zéro n'aurait aucun sens. Si vous préférez ne jamais dépasser, une case dans vos réglages arrête la mesure une fois votre forfait utilisé : les demandes continuent d'arriver avec l'adresse et le contact, vous ne perdez aucun prospect, et vous lancez la mesure vous-même quand elle en vaut la peine.",
    },
    {
      q: "Les prix montrés à mon client sont-ils les miens ?",
      r: "Entièrement. Vous entrez vos matériaux, vos taux de main-d'œuvre, votre pourcentage de pertes, vos taxes et votre marge. Le système ne fait que le calcul ; les chiffres restent les vôtres, et la soumission sort avec votre logo et vos coordonnées.",
    },
    {
      q: "Est-ce que ça fonctionne partout ?",
      r: "Au Canada et aux États-Unis. La mesure la plus précise couvre la majorité des zones habitées ; ailleurs, l'outil bascule automatiquement sur la détection par image satellite. Vous obtenez une mesure dans les deux cas.",
    },
    {
      q: "L'estimateur sur mon site web, c'est compliqué à installer ?",
      r: "Deux lignes de code à coller, fournies dans votre tableau de bord. Il s'adapte aux couleurs et au thème de votre site, en français ou en anglais. Chaque visiteur qui l'utilise devient un prospect avec son adresse et ses coordonnées.",
    },
    {
      q: "Puis-je changer de forfait ou annuler ?",
      r: "En tout temps, depuis votre espace de facturation. Aucun contrat, aucune pénalité. En passant du mensuel à l'annuel, vous économisez jusqu'à 30 %.",
    },
    {
      q: "Comment ça se passe si je prends le forfait avec le site web ?",
      r: "Un membre de notre équipe vous contacte et discute de vos besoins afin d'élaborer un cahier des charges. Une fois celui-ci accepté, votre site est complété en moins de 5 jours ouvrables.",
    },
    {
      q: "Peut-on faire baisser son abonnement ?",
      r: "Oui, et il peut même tomber à zéro. La mécanique est expliquée dans votre tableau de bord, une fois votre compte ouvert.",
    },
  ],
  en: [
    {
      q: "How accurate are the satellite measurements?",
      r: "The roof is measured from satellite imagery and the building's real pitch, not eyeballed. And nothing is forced on you: the outline stays editable by hand before the quote is produced, and so does the pitch. You keep the final say on every number.",
    },
    {
      q: "Can I try it before paying?",
      r: "Yes. The estimator on our home page is the real tool running on a real account: enter an address you know and compare the result to what you would have quoted. No credit card, no sign-up.",
    },
    {
      q: "What happens if I go over my estimate count?",
      r: "You decide. By default extra estimates go through, billed at your plan's overage rate — $0.50 to $2 depending on the plan — because losing a job to a counter at zero would make no sense. If you would rather never go over, a checkbox in your settings stops the measuring once your plan is used up: requests keep coming in with the address and contact, you lose no leads, and you run the measurement yourself when it's worth it.",
    },
    {
      q: "Are the prices shown to my client mine?",
      r: "Entirely. You enter your materials, labour rates, waste percentage, taxes and margin. The system only does the math; the numbers stay yours, and the quote comes out with your logo and contact details.",
    },
    {
      q: "Does it work everywhere?",
      r: "Across Canada and the United States. The most precise measurement covers most populated areas; elsewhere the tool automatically falls back to satellite image detection. Either way you get a measurement.",
    },
    {
      q: "Is the estimator hard to install on my website?",
      r: "Two lines of code to paste, provided in your dashboard. It matches your site's colours and theme, in French or English. Every visitor who uses it becomes a lead, with their address and contact details.",
    },
    {
      q: "Can I change plans or cancel?",
      r: "Any time, from your billing portal. No contract, no penalty. Switching from monthly to yearly saves you up to 30%.",
    },
    {
      q: "What happens if I take the plan with the website?",
      r: "A member of our team contacts you and goes over your needs to put together a written brief. Once you approve it, your site is completed in under 5 business days.",
    },
    {
      q: "Can I bring my subscription down?",
      r: "Yes — it can even drop to zero. How it works is laid out in your dashboard once your account is open.",
    },
  ],
  es: [
    {
      q: "¿Qué tan precisas son las mediciones satelitales?",
      r: "El techo se mide a partir de imágenes satelitales y de la pendiente real del edificio, no a ojo. Y nada se le impone: el contorno sigue siendo editable a mano antes de producir la cotización, igual que la pendiente. Usted tiene la última palabra sobre cada cifra.",
    },
    {
      q: "¿Puedo probarlo antes de pagar?",
      r: "Sí. El estimador de nuestra página de inicio es la herramienta real funcionando en una cuenta real: ingrese una dirección que conozca y compare el resultado con lo que usted habría cotizado. Sin tarjeta de crédito, sin registro.",
    },
    {
      q: "¿Qué pasa si supero mi número de estimados?",
      r: "Usted decide. Por defecto los estimados adicionales pasan, facturados a la tarifa de excedente de su plan — de $0.50 a $2 según el plan — porque perder un contrato por un contador en cero no tendría sentido. Si prefiere no excederse nunca, una casilla en su configuración detiene la medición una vez usado su plan: las solicitudes siguen llegando con la dirección y el contacto, no pierde ningún prospecto, y usted lanza la medición cuando vale la pena.",
    },
    {
      q: "¿Los precios que ve mi cliente son los míos?",
      r: "Totalmente. Usted ingresa sus materiales, sus tarifas de mano de obra, su porcentaje de desperdicio, sus impuestos y su margen. El sistema solo hace el cálculo; las cifras siguen siendo suyas, y la cotización sale con su logotipo y sus datos de contacto.",
    },
    {
      q: "¿Funciona en todas partes?",
      r: "En Canadá y Estados Unidos. La medición más precisa cubre la mayoría de las zonas pobladas; en el resto, la herramienta pasa automáticamente a la detección por imagen satelital. En ambos casos obtiene una medición.",
    },
    {
      q: "¿Es complicado instalar el estimador en mi sitio web?",
      r: "Dos líneas de código para pegar, provistas en su panel. Se adapta a los colores y al tema de su sitio, en francés o en inglés. Cada visitante que lo usa se convierte en un prospecto, con su dirección y sus datos de contacto.",
    },
    {
      q: "¿Puedo cambiar de plan o cancelar?",
      r: "En cualquier momento, desde su portal de facturación. Sin contrato, sin penalidad. Al pasar del plan mensual al anual, ahorra hasta un 30%.",
    },
    {
      q: "¿Cómo funciona si tomo el plan con el sitio web?",
      r: "Un miembro de nuestro equipo lo contacta y conversa sobre sus necesidades para elaborar un pliego de requisitos. Una vez aprobado, su sitio queda listo en menos de 5 días hábiles.",
    },
    {
      q: "¿Se puede reducir la suscripción?",
      r: "Sí, e incluso puede bajar a cero. La mecánica se explica en su panel, una vez abierta su cuenta.",
    },
  ],
};

// Titres de la section, hors dictionnaire global : le composant est autonome.
const TITRES = {
  fr: { titre: "Questions fréquentes", sous: "Il en manque une ? Écrivez-nous." },
  en: { titre: "Frequently asked questions", sous: "Missing one? Write to us." },
  es: { titre: "Preguntas frecuentes", sous: "¿Falta alguna? Escríbanos." },
};

export default function Faq({ className = "" }) {
  const { lang } = useI18n();
  // Trois langues : fr si "fr", es si "es", anglais pour tout le reste.
  const liste = QUESTIONS[lang] || QUESTIONS.en;
  const titres = TITRES[lang] || TITRES.en;

  return (
    <section className={className} data-testid="faq">
      <h2 className="font-heading text-3xl font-extrabold tracking-tighter text-center mb-2">
        {titres.titre}
      </h2>
      <p className="text-ink-soft text-center text-sm mb-10">
        {titres.sous}
      </p>

      <div className="max-w-3xl mx-auto divide-y divide-line border-y border-line">
        {liste.map((item, i) => (
          <details key={i} className="group py-1">
            <summary className="flex items-center justify-between gap-6 cursor-pointer list-none py-4 font-medium hover:text-brand-ink transition-colors">
              <span>{item.q}</span>
              <ChevronDown className="h-4 w-4 shrink-0 text-ink-faint transition-transform duration-200 group-open:rotate-180" />
            </summary>
            <p className="text-ink-soft text-sm leading-relaxed pb-5 pr-10">{item.r}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
